// Remembers which `/for/:audience` front door a visitor arrived through, so a
// guest session created later can be provisioned into that partner's org.
//
// #2050: 457 Yoma Navigator runs accumulated in org 2 "Trial" while org 10
// "Yoma" held 0 runs and 0 users, because `createGuestUser` hardcoded the trial
// org and had no idea where the visitor came from.
//
// Why the ROUTE and not the scenario: a scenario is not a front door. An org
// can own scenarios that get shared around without wanting every visitor
// claimed as theirs, and a partner's front door often exists before anyone has
// decided which scenario sits behind it. `/for/:audience` is an explicit,
// deliberate entry point — and the backend already reasons about it for
// cross-org scenario access (see `runsv2.js`, "Guest users from /for/:audience
// landing pages").
//
// The server treats whatever this returns as an untrusted lookup key and checks
// it against its own allowlist, so a stale or forged value cannot place someone
// in an arbitrary org.

const STORAGE_KEY = 'tacit_landing_audience';

// `/for/yoma` → "yoma". A trailing PATH SEGMENT is fine — someone deep-linked to
// `/for/yoma/something` still came through the Yoma door.
//
// Codex review catch: the boundary is load-bearing. Without `(?:\/|$)` this is a
// PREFIX match, so `/for/yoma.evil` and `/for/yoma%2Fother` would also capture
// "yoma" (the character class stops at the `.` or `%`) and route a visitor from
// an unrelated URL into the Yoma org. Anchored to a complete segment, those fall
// through to null → `trial`, which is the correct answer for an audience nobody
// configured.
const FOR_ROUTE = /^\/for\/([A-Za-z0-9_-]+)(?:\/|$)/;

function parseAudience(pathname) {
  const match = FOR_ROUTE.exec(pathname || '');
  return match ? match[1].toLowerCase() : null;
}

// sessionStorage throws in Safari private mode and is absent in SSR/tests.
// A missing audience is a clean fallback (guest goes to `trial`), never an error.
function safeRead() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function safeWrite(value) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Ignore — falling back to `trial` is correct behaviour, not a failure.
  }
}

/**
 * Record the front door for this session if `pathname` is one.
 * Exported for tests and for callers that know the path explicitly.
 * @returns {string|null} the audience captured, or null
 */
export function captureLandingAudience(pathname) {
  const audience = parseAudience(pathname);
  if (audience) safeWrite(audience);
  return audience;
}

/**
 * The front door for this session, or null.
 *
 * Checks the CURRENT path first so a same-session navigation to `/for/...` is
 * picked up even though the module already ran at boot, then falls back to what
 * was stored on arrival — which is the case that actually matters, since a
 * visitor usually lands on `/for/yoma` and starts a scenario a few clicks later.
 */
export function getLandingAudience() {
  if (typeof window === 'undefined') return null;
  return captureLandingAudience(window.location?.pathname) || safeRead();
}

// `/scenarios/594` → 594. Anchored to a complete numeric segment for the same
// reason as FOR_ROUTE: a prefix match would let `/scenarios/594x` resolve to 594.
const SCENARIO_ROUTE = /^\/scenarios\/(\d+)(?:\/|$)/;

/**
 * The scenario this session entered through, or null.
 *
 * This is the path that actually carries the traffic: Yoma learners arrive from
 * yoma.world on a direct link to the Navigator scenario and never see
 * `/for/yoma`. Resolving on the audience alone would have left the real
 * population in `trial`.
 *
 * Read live from the URL rather than persisted: unlike the front door — which
 * must survive several clicks before a guest session is created — a scenario
 * deep-link creates the guest ON that page, so the current path is the right
 * answer and a stale stored value would be the wrong one.
 *
 * The server treats this as untrusted and still requires the scenario's owning
 * org to be allowlisted, so a wrong or forged id cannot reassign anyone.
 */
export function getEntryScenarioId() {
  if (typeof window === 'undefined') return null;
  const match = SCENARIO_ROUTE.exec(window.location?.pathname || '');
  return match ? Number(match[1]) : null;
}

/**
 * #2673 — a stable string identifying WHICH entry point this guest session
 * is being created through, used to decide whether a stored guest token may
 * be reused instead of minting a fresh one (see AuthContext.loginAsGuest).
 *
 * This is deliberately the SAME two signals `createGuestSession` already
 * sends the server (audience, entry scenario) — not a re-resolution of the
 * organization those signals map to. The frontend cannot resolve org
 * membership itself (that requires the DB-backed allowlist check
 * `AuthService.resolveGuestOrganization` does server-side); comparing entry
 * points instead is a conservative proxy: identical entry point ⇒
 * `resolveGuestOrganization` is a pure function of {audience, scenarioId}
 * plus DB state, so it resolves to the SAME org today as it did when the
 * stored token was minted, in every case except a rare, one-time org
 * rescope of that exact scenario/audience (which last happened during
 * #2050/#1844's Yoma org migration). A false "different" here only ever
 * costs an unnecessary re-mint — never a wrong-org reuse — and the
 * server-side row-derived organization_id fix (#2673, middleware/auth.js)
 * is the actual security backstop regardless: even a wrongly-reused token's
 * `req.organizationId` is read live from its CURRENT row on every request,
 * not trusted from this client-side check.
 *
 * Returns null when neither signal is present (no entry context — the
 * existing unconditional-mint behaviour is preserved for that case).
 */
export function getGuestEntryKey() {
  const audience = getLandingAudience();
  if (audience) return `audience:${audience}`;
  const scenarioId = getEntryScenarioId();
  if (scenarioId) return `scenario:${scenarioId}`;
  return null;
}

// Capture the ENTRY url once at boot. By the time a guest session is created
// the visitor has usually navigated away, so reading the path at that moment
// would miss the door they actually came through.
if (typeof window !== 'undefined') {
  captureLandingAudience(window.location?.pathname);
}
